import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import Person from "@/src/components/Form/Common/Person/Person";
import Grid from "@mui/material/Grid";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import Address from "@/src/components/Form/Common/Address/Address";
import Group from "@/src/components/Wizard/Group";
import { useForm } from "@/src/provider/Form/FormProvider";
import { dateFieldDefault, isLessThen18YearsAgo, pastOrSameDateDefault, requiredFieldDefault, requiredValidator } from "@/src/services/validationRules";
import { translate } from "@/src/services/translations/translations";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import OtherPersonIcon from "@/src/assets/icons/contract/toggleable/IconMultiplePeople";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import React from "react";
import { translatedModalContent } from "@/src/services/formStepUtils";

type CoinsuredClientFieldsProps = {
  product: AbstractCase;
};

export default function CoinsuredClient({ product }: CoinsuredClientFieldsProps) {
  const { values } = useForm();

  return (
    <>
      <Grid size={12}>
        <Group
          type="radio"
          name="clientWasMinorAtContractDate"
          question={"Ist die Person zum gegenwärtigen Zeitpunkt minderjährig?"}
          options={[
            {
              labelText: "Ja",
              labelIcon: <CheckIcon />,
              labelIconActive: <CheckIcon active />,
              value: "yes",
            },
            {
              labelText: "Nein",
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "no",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      <Person path="client" headline={translate(`${product.name}.coinsuredClientFields.client.headline`, values)} />
      {values.clientWasMinorAtContractDate === "yes" && (
        <Grid size={12}>
          <ValidatorDateField
            name="client.birthDate"
            label="Geburtstag Vertragsschließender"
            validators={[
              requiredFieldDefault,
              dateFieldDefault,
              pastOrSameDateDefault,
              {
                validator: isLessThen18YearsAgo,
                message: "Die Person ist bereits volljährig. Bitte Datum anpassen oder vorherige Frage mit “Nein” beantworten",
              },
            ]}
          />
        </Grid>
      )}
      <Address path="client.residenceAddress" headline={translate(`${product.name}.coinsuredClientFields.client.residenceAddress.headline`, values)} />
      {values.clientWasMinorAtContractDate === "yes" && (
        <>
          <Grid size={12}>
            <Group
              type="radio"
              name="clientLegalRepresentativeAmount"
              modalContent={translatedModalContent(`${product.name}.clientLegalRepresentativeAmount.modalContent.paragraphs`, values)}
              question={translate(`${product.name}.clientLegalRepresentativeAmount.question`, values)}
              options={[
                {
                  labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.1.label`, values),
                  labelIcon: <PersonIcon />,
                  labelIconActive: <PersonIcon active />,
                  value: "1",
                },
                {
                  labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.2.label`, values),
                  labelIcon: <OtherPersonIcon />,
                  labelIconActive: <OtherPersonIcon active />,
                  value: "2",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          <Grid size={12}>
            <Group
              type="radio"
              name="legalRepresentativeType"
              question={translate(`${product.name}.legalRepresentativeType.question`, values)}
              options={[
                {
                  labelText: translate(`${product.name}.legalRepresentativeType.options.me.label`, values),
                  labelIcon: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon /> : <PersonIcon />,
                  labelIconActive: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon active /> : <PersonIcon active />,
                  value: "me",
                },
                {
                  labelText: translate(`${product.name}.legalRepresentativeType.options.other.label`, values),
                  labelIcon: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon /> : <OtherPersonIcon />,
                  labelIconActive: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon active /> : <OtherPersonIcon active />,
                  value: "other",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
          <Person path="clientLegalRepresentative" headline={translate(`${product.name}.clientLegalRepresentative.question`, values)} />
          <Address
            path="clientLegalRepresentative.residenceAddress"
            headline={translate(`${product.name}.clientLegalRepresentative.residenceAddress.question`, values)}
          />
          {values.clientLegalRepresentativeAmount === "2" && (
            <>
              <Person path="secondClientLegalRepresentative" headline={translate(`${product.name}.secondClientLegalRepresentative.question`, values)} />
              <Address
                path="secondClientLegalRepresentative.residenceAddress"
                headline={translate(`${product.name}.secondClientLegalRepresentative.residenceAddress.question`, values)}
              />

              <Grid size={12}>
                <Group
                  type="radio"
                  name="clientLegalRepresentativesAuthorizationType"
                  question={translate(`${product.name}.clientLegalRepresentativesAuthorizationType.question`, values)}
                  options={[
                    {
                      labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.both.label`, values),
                      labelIcon: <OtherPersonIcon />,
                      labelIconActive: <OtherPersonIcon active />,
                      value: "both",
                    },
                    {
                      labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.individually.label`, values),
                      labelIcon: <PersonIcon />,
                      labelIconActive: <PersonIcon active />,
                      value: "individually",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
}
